import { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useParams, useNavigate } from 'react-router-dom';
import { Typography, Button, Stack, FormControl, OutlinedInput, Rating, Divider, Grid } from '@mui/material';
import StarBorderIcon from '@mui/icons-material/StarBorder';
import { useTranslation } from 'react-i18next';
import { useAddNewReviewMutation, useGetReviewsQuery, useDeleteReviewMutation, useUpdateReviewMutation } from '../features/api/rest/apiReviewsSlice';
import { useDeleteFeatureReviewMutation, useGetPersonalFeatureReviewsQuery, useUpdateFeatureReviewMutation } from '../features/api/rest/apiFeatureReviewsSlice';
import { useAddNewReviewImageMutation, useGetReviewImagesQuery } from '../features/api/rest/apiReviewsImagesSlice';
import RadioFeature from './RadioFeature';
import Spinner from './Spinner';
import Error from './Error';
import AddReviewPhoto from './AddReviewPhoto';
import { setAlertMessage } from '../features/layoutSlice';

function EditOrCreateReview() {
  const [reviewId, setReviewId] = useState('');
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [rating, setRating] = useState(0);

  const [valueForMoney, setValueForMoney] = useState(null);
  const [healthiness, setHealthiness] = useState(null);
  const [taste, setTaste] = useState(null);
  const [texture, setTexture] = useState(null);
  const [packaging, setPackaging] = useState(null);
  const [durability, setDurability] = useState(null);
  const [availability, setAvailability] = useState(null);

  const imagesArray = useSelector((state) => state.imagesReview.imagesArray);

  const { ean } = useParams();
  const navigate = useNavigate();
  const { t } = useTranslation();
  const dispatch = useDispatch();

  const { review, isLoadingFetch, isFetching, isError } = useGetReviewsQuery(
    { ean, mine: true },
    {
      selectFromResult: ({ data }) => ({
        review: data?.results[0],
      }),
      refetchOnMountOrArgChange: true,
    }
  );

  const { features } = useGetPersonalFeatureReviewsQuery(ean, {
    selectFromResult: ({ data }) => ({
      features: data,
    }),
    refetchOnMountOrArgChange: true,
  });

  const { data: images } = useGetReviewImagesQuery(
    { ean, rpk: review?.id },
    {
      skip: !review,
    }
  );

  // react-redux hooks
  const [addNewReview, { isLoading }] = useAddNewReviewMutation();
  const [updateReview, { isLoadingUpload }] = useUpdateReviewMutation();
  const [deleteReview, { isLoadingDelete }] = useDeleteReviewMutation();

  const [updateFeatureReview] = useUpdateFeatureReviewMutation();
  const [deleteFeatureReview] = useDeleteFeatureReviewMutation();

  const [addPhotoToReview, { isLoadingAddPhoto }] = useAddNewReviewImageMutation();

  useEffect(() => {
    if (review) {
      setReviewId(review.id);
      setTitle(review.title);
      setDescription(review.description);
      setRating(review.rating);
    } else {
      setReviewId('');
      setTitle('');
      setDescription('');
      setRating(0);
    }
    dispatch({ type: 'imagesReview/remove' });
  }, [review]);

  useEffect(() => {
    if (checkFeatures(features)) {
      setValueForMoney(features?.value_for_money);
      setHealthiness(features?.healthiness);
      setTaste(features?.taste);
      setTexture(features?.texture);
      setPackaging(features?.packaging);
      setDurability(features?.durability);
      setAvailability(features?.availability);
    }
  }, [features]);

  const checkFeatures = (obj) => {
    return obj && Object.keys(features).length !== 0;
  };

  const onSubmit = async (e) => {
    e.preventDefault();
    let response;
    try {
      await updateFeatureReview({ payload: { value_for_money: valueForMoney, healthiness, taste, texture, packaging, durability, availability }, ean }).unwrap();
      if (!review) {
        response = await addNewReview({ payload: { title, description, rating }, ean }).unwrap();
      } else {
        response = await updateReview({ payload: { title, description, rating }, ean, reviewId }).unwrap();
      }
      if (response && imagesArray.length > 0) {
        imagesArray[0].map(async (image) => {
          const formData = new FormData();
          formData.append('image', image);
          await addPhotoToReview({ ean, rpk: response.id, image: formData }).unwrap();
        });
      }
      dispatch(setAlertMessage({ text: t('reviews.messages.success.uploading'), type: 'success' }));
      navigate(`/products/${ean}`);
    } catch (err) {
      dispatch(setAlertMessage({ text: t('reviews.messages.error.uploading'), type: 'error' }));
    }
  };

  const handleClick = () => {
    navigate(`/products/${ean}`);
  };

  const handleDelete = async (e) => {
    e.preventDefault();
    try {
      await deleteFeatureReview({ ean }).unwrap();
      await deleteReview({ ean, reviewId }).unwrap();
      dispatch(setAlertMessage({ text: t('reviews.messages.success.delete'), type: 'success' }));
      navigate(`/products/${ean}`);
    } catch (err) {
      dispatch(setAlertMessage({ text: t('reviews.messages.error.delete'), type: 'error' }));
    }
  };

  const handleDeleteFeatures = async (e) => {
    e.preventDefault();
    try {
      await deleteFeatureReview({ ean }).unwrap();
      setValueForMoney(null);
      setHealthiness(null);
      setTaste(null);
      setTexture(null);
      setPackaging(null);
      setDurability(null);
      setAvailability(null);
      dispatch(setAlertMessage({ text: t('reviews.messages.success.delete'), type: 'success' }));
    } catch (err) {
      dispatch(setAlertMessage({ text: t('reviews.messages.error.deleteFeature'), type: 'error' }));
    }
  };

  if (isLoading || isLoadingFetch || isFetching || isLoadingDelete || isLoadingUpload || isLoadingAddPhoto) return <Spinner />;
  if (isError) return <Error />;

  return (
    <Stack component="form" onSubmit={onSubmit} sx={{ mr: { xs: '10%', md: '20%' }, ml: { xs: '10%', md: '20%' }, pb: 1 }}>
      <Typography variant="h5" sx={{ fontWeight: 'bold', pb: 2, alignSelf: 'center' }}>
        {t('reviews.form.heading')}
      </Typography>
      <Typography variant="h6" align="left" sx={{ pb: 1, fontWeight: 'bold' }}>
        {t('reviews.form.fields.rating')}
      </Typography>
      <FormControl>
        <Rating sx={{ backgroundColor: '', mb: 1.5 }} name="simple-controlled" id="reviewRating" required value={Number(rating)} onChange={(e) => setRating(e.target.value)} emptyIcon={<StarBorderIcon />} />
      </FormControl>
      <Divider variant="middle" sx={{ mb: 2, mt: 1 }} />
      <Typography variant="h6" align="left" sx={{ pb: 1, fontWeight: 'bold' }}>
        {t('reviews.features.evaluate')}
      </Typography>
      <Typography variant="h7" align="left" sx={{ pb: 1, pt: 2, fontWeight: 'bold' }}>
        {t('reviews.features.valueForMoney')}
      </Typography>
      <RadioFeature setValue={setValueForMoney} value={Number(valueForMoney)} />
      <Typography variant="h7" align="left" sx={{ pb: 1, pt: 2, fontWeight: 'bold' }}>
        {t('reviews.features.healthiness')}
      </Typography>
      <RadioFeature setValue={setHealthiness} value={Number(healthiness)} />
      <Typography variant="h7" align="left" sx={{ pb: 1, pt: 2, fontWeight: 'bold' }}>
        {t('reviews.features.taste')}
      </Typography>
      <RadioFeature setValue={setTaste} value={Number(taste)} />
      <Typography variant="h7" align="left" sx={{ pb: 1, pt: 2, fontWeight: 'bold' }}>
        {t('reviews.features.texture')}
      </Typography>
      <RadioFeature setValue={setTexture} value={Number(texture)} />
      <Typography variant="h7" align="left" sx={{ pb: 1, pt: 2, fontWeight: 'bold' }}>
        {t('reviews.features.packaging')}
      </Typography>
      <RadioFeature setValue={setPackaging} value={Number(packaging)} />
      <Typography variant="h7" align="left" sx={{ pb: 1, pt: 2, fontWeight: 'bold' }}>
        {t('reviews.features.durability')}
      </Typography>
      <RadioFeature setValue={setDurability} value={Number(durability)} />
      <Typography variant="h7" align="left" sx={{ pb: 1, pt: 2, fontWeight: 'bold' }}>
        {t('reviews.features.availability')}
      </Typography>
      <RadioFeature setValue={setAvailability} value={Number(availability)} />
      <Divider variant="middle" sx={{ mb: 2, mt: 1, pt: 2 }} />
      <Typography variant="h6" align="left" sx={{ pb: 1, fontWeight: 'bold' }}>
        {t('reviews.form.fields.title')}
      </Typography>
      <FormControl>
        <OutlinedInput sx={{ borderRadius: 0, mb: 2 }} type="text" id="reviewTitle" placeholder={t('reviews.form.placeholders.title')} value={title} onChange={(e) => setTitle(e.target.value)} />
      </FormControl>
      <Divider variant="middle" sx={{ mb: 2, mt: 1 }} />
      <Typography variant="h6" align="left" sx={{ pb: 1, fontWeight: 'bold' }}>
        {t('reviews.form.fields.description')}
      </Typography>
      <FormControl>
        <OutlinedInput
          sx={{ backgroundColor: 'white', borderRadius: 0, mb: 2, pt: 0.6, pl: 0.4, pr: 0 }}
          type="text"
          multiline
          minRows={4}
          maxRows={10}
          id="reviewDescription"
          placeholder={t('reviews.form.placeholders.description')}
          value={description}
          onChange={(e) => setDescription(e.target.value)}
        />
      </FormControl>
      <AddReviewPhoto images={images} reviewID={review?.id} ean={ean} />
      <Divider variant="middle" sx={{ borderColor: 'white' }} />
      <Grid container spacing={1} sx={{ textAlign: 'center' }}>
        <Grid item xs={12} sm={6}>
          {(review || checkFeatures(features)) && (
            <Button onClick={handleDelete} variant="contained">
              {t('reviews.delete')}
            </Button>
          )}
        </Grid>
        <Grid item xs={12} sm={6}>
          {checkFeatures(features) && (
            <Button onClick={handleDeleteFeatures} variant="contained">
              {t('reviews.features.delete')}
            </Button>
          )}
        </Grid>
        <Grid item xs={12} sm={6}>
          <Button type="submit" variant="contained" disabled={rating === 0}>
            {t('common.actions.send')}
          </Button>
        </Grid>
        <Grid item xs={12} sm={6}>
          <Button variant="contained" onClick={handleClick}>
            {t('reviews.form.return')}
          </Button>
        </Grid>
      </Grid>
    </Stack>
  );
}

export default EditOrCreateReview;
